import styled from "@emotion/styled/macro";
import {
  Typography,
  tokens,
  useSingletonMobileMediaQuery,
} from "@sunrun/experience-ui-components";
import { ReactNode } from "react";
import {
  ReviewMonthlySummary,
  ReviewOfferProduct,
} from "../../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { formatCurrency, formatPercent } from "../../../utils/formatHelpers";
import { NEW_HOMES_MONTHLY_AUTOPAY_DISCOUNT_TEXT } from "../../../constants/newHomes";
import {
  getBatteryYearOneMonthlyPayment,
  getSolarYearOneMonthlyPayment,
} from "./offerReviewPaymentProductAmounts";
import {
  PaymentCard,
  PaymentColumn,
  PaymentColumns,
  PaymentFinePrint,
  PaymentGroup,
  PaymentGroupTitle,
  PaymentLineItem,
  PaymentRows,
  PaymentValidUntil,
  PaymentValue,
} from "./OfferReviewPaymentPrimitives";
import {
  COMPARABLE_RETAIL_BATTERY_FOOTNOTE,
  ESTIMATED_UTILITY_BILL_PAYMENTS_TOOLTIP,
} from "./offerReviewPaymentCopy";
import { OfferReviewInfoTooltip } from "components/atoms/OfferReviewInfoTooltip";

const STANDARD_MONTHLY_PAYMENT_DISCLAIMER = "Includes 5% ACH discount";

type MonthlyPaymentDetailsProps = {
  summary: ReviewMonthlySummary;
  products?: ReviewOfferProduct[];
  isNewHomes: boolean;
  omitInlineMonthlyAutopayDisclaimer: boolean;
  showValidUntil: boolean;
  children?: ReactNode;
};

const MonthlyPaymentDetails = ({
  summary,
  products,
  isNewHomes,
  omitInlineMonthlyAutopayDisclaimer,
  showValidUntil,
  children,
}: MonthlyPaymentDetailsProps) => {
  const isMobile = useSingletonMobileMediaQuery();
  const batteryPayment = getBatteryYearOneMonthlyPayment(products);
  const sunrunEnergyPayment =
    getSolarYearOneMonthlyPayment(products) ??
    summary.firstYearMonthly - (batteryPayment ?? 0);
  const autopayDisclaimer = isNewHomes
    ? NEW_HOMES_MONTHLY_AUTOPAY_DISCOUNT_TEXT
    : STANDARD_MONTHLY_PAYMENT_DISCLAIMER;

  const estimatedUtilityBillPayments = (
    <PaymentGroup>
      <GroupTitleRow>
        <PaymentGroupTitle
          size={14}
          fontWeight="medium"
          color={tokens.BRAND_HERO_BLUE}
        >
          Estimated Utility Bill Payments
        </PaymentGroupTitle>
        <OfferReviewInfoTooltip
          content={ESTIMATED_UTILITY_BILL_PAYMENTS_TOOLTIP}
          ariaLabel="About estimated utility bill payments"
          maxWidth={480}
          iconSize={14}
          testId="estimated-utility-bill-tooltip"
        />
      </GroupTitleRow>
      <PaymentRows>
        <PaymentLineItem
          label="Avg. est. pre-Sunrun utility bill"
          value={formatCurrency(summary.preSolarUtility, 2)}
          unit="/mo"
          valueSize={14}
          valueLineHeight={1.4}
        />
        <PaymentLineItem
          label="Avg. est. post-Sunrun utility bill"
          value={formatCurrency(summary.postSolarUtility, 2)}
          unit="/mo"
          valueSize={14}
          valueLineHeight={1.4}
        />
      </PaymentRows>
    </PaymentGroup>
  );

  const lockingInPayments = (
    <PaymentGroup>
      <PaymentGroupTitle
        size={14}
        fontWeight="medium"
        color={tokens.BRAND_HERO_BLUE}
      >
        Locking in payments with your Agreement Terms:
      </PaymentGroupTitle>
      <PaymentRows>
        <PaymentLineItem
          label="Annual escalator on monthly payment"
          value={formatPercent(summary.annualEscalator)}
          valueSize={14}
          valueLineHeight={1.4}
        />
        <PaymentLineItem
          label="Agreement term"
          value={String(summary.termYears)}
          unit="years"
          valueSize={14}
          valueLineHeight={1.4}
        />
        <DueAtSigning $size={14} $weight={600} $lineHeight={1.4}>
          {formatCurrency(summary.dueAtSigning, 0)} due at signing
        </DueAtSigning>
      </PaymentRows>
    </PaymentGroup>
  );

  const promotionalBatteryPayment = batteryPayment != null && (
    <PaymentGroup $tight>
      <PaymentRows>
        <PaymentLineItem
          label="Promotional battery payment"
          labelSize={20}
          value={formatCurrency(batteryPayment, 2)}
          unit="/mo"
          valueSize={20}
        />
      </PaymentRows>
      <PaymentFinePrint data-testid="offer-review-payment-group-footnote">
        {COMPARABLE_RETAIL_BATTERY_FOOTNOTE}
      </PaymentFinePrint>
    </PaymentGroup>
  );

  const sunrunEnergyPaymentGroup = (
    <PaymentGroup>
      <PaymentRows>
        <EnergyPaymentLineItem
          label="Year 1 Sunrun energy payment"
          labelSize={20}
          value={formatCurrency(sunrunEnergyPayment, 2)}
          unit="/mo"
          valueSize={20}
          maxLabelWidth={400}
          labelFootnote={
            omitInlineMonthlyAutopayDisclaimer ? undefined : (
              <AutopayDisclaimer size={12}>
                {autopayDisclaimer}
              </AutopayDisclaimer>
            )
          }
        />
      </PaymentRows>
    </PaymentGroup>
  );

  return (
    <PaymentCard $opacity={0.4} data-testid="offer-review-simplified-payment">
      <PaymentColumn>
        <PaymentRows>
          <PaymentColumns $isMobile={isMobile} $columnGap={32}>
            <PaymentColumn>{sunrunEnergyPaymentGroup}</PaymentColumn>
            <PaymentColumn>{promotionalBatteryPayment}</PaymentColumn>
          </PaymentColumns>
        </PaymentRows>
        <PaymentRows>
          <PaymentColumns $isMobile={isMobile} $columnGap={32}>
            <PaymentColumn>{estimatedUtilityBillPayments}</PaymentColumn>
            <PaymentColumn>{lockingInPayments}</PaymentColumn>
          </PaymentColumns>
        </PaymentRows>
      </PaymentColumn>
      {children}
      {showValidUntil && (
        <ValidUntilRow>
          <RightAlignedValidUntil expires={summary.expires} />
        </ValidUntilRow>
      )}
    </PaymentCard>
  );
};

const GroupTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const EnergyPaymentLineItem = styled(PaymentLineItem)``;

const AutopayDisclaimer = styled(Typography)`
  && {
    max-width: 352px;
    color: ${tokens.TINTS_OFF_WHITE_20};
    line-height: 1 !important;
    letter-spacing: -0.02em;
  }
`;

const DueAtSigning = styled(PaymentValue)`
  align-self: flex-end;
`;

const ValidUntilRow = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  padding-top: 4px;
`;

const RightAlignedValidUntil = styled(PaymentValidUntil)`
  && {
    line-height: 1 !important;
    text-align: right;
  }
`;

export { MonthlyPaymentDetails };
